import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, Btn, Modal, Field, Input, Select, StatusPill } from "@/components/ui-kit";
import { formatDate } from "@/lib/format";
import { Plus, Trash2, Edit3, Car as CarIcon } from "lucide-react";

const empty = {
  registration_number: "", vehicle_name: "", vehicle_type: "Sedan",
  brand: "", model: "", seating_capacity: 4, fuel_type: "Diesel",
  ownership: "Company", vendor_name: "",
  rc_expiry: "", insurance_expiry: "", fitness_expiry: "", permit_expiry: "", puc_expiry: "",
  status: "available",
};

export default function Vehicles() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => setItems((await api.get("/owner/vehicles")).data);
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e?.preventDefault();
    const data = { ...editing, seating_capacity: Number(editing.seating_capacity) };
    // strip empty date strings -> null
    ["rc_expiry","insurance_expiry","fitness_expiry","permit_expiry","puc_expiry"].forEach(k => { if (!data[k]) data[k] = null; });
    if (data.id) await api.patch(`/owner/vehicles/${data.id}`, data);
    else await api.post("/owner/vehicles", data);
    setOpen(false); setEditing(null); load();
  };

  const del = async (id) => {
    if (!confirm("Delete vehicle?")) return;
    await api.delete(`/owner/vehicles/${id}`); load();
  };

  return (
    <div data-testid="vehicles-page">
      <PageHeader title="Vehicles" subtitle="Master list of every vehicle in your fleet."
        actions={<Btn data-testid="vehicle-add" onClick={() => { setEditing({...empty}); setOpen(true); }}><Plus className="h-4 w-4" /> Add Vehicle</Btn>}
      />
      <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden">
        <table className="tbl">
          <thead><tr><th>Reg #</th><th>Vehicle</th><th>Type</th><th>Fuel</th><th>Seats</th><th>Insurance Exp.</th><th>PUC Exp.</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan={9} className="text-center py-10 text-zinc-500">
                <CarIcon className="h-8 w-8 mx-auto mb-2 text-zinc-300" /> No vehicles yet.
              </td></tr>
            ) : items.map((v) => (
              <tr key={v.id} data-testid={`vehicle-row-${v.id}`}>
                <td className="font-mono font-bold text-asphalt">{v.registration_number}</td>
                <td>{v.vehicle_name} <div className="text-xs text-zinc-500">{v.brand} {v.model}</div></td>
                <td>{v.vehicle_type}</td>
                <td>{v.fuel_type}</td>
                <td>{v.seating_capacity}</td>
                <td>{formatDate(v.insurance_expiry)}</td>
                <td>{formatDate(v.puc_expiry)}</td>
                <td><StatusPill status={v.status} /></td>
                <td className="text-right">
                  <Btn variant="ghost" data-testid={`vehicle-edit-${v.id}`} onClick={() => { setEditing(v); setOpen(true); }}><Edit3 className="h-4 w-4" /></Btn>
                  <Btn variant="ghost" data-testid={`vehicle-delete-${v.id}`} onClick={() => del(v.id)}><Trash2 className="h-4 w-4 text-red-600" /></Btn>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal open={open} size="lg" onClose={() => setOpen(false)} title={editing?.id ? "Edit Vehicle" : "Add Vehicle"} testid="vehicle-modal"
        footer={<>
          <Btn variant="outline" onClick={() => setOpen(false)}>Cancel</Btn>
          <Btn data-testid="vehicle-save" onClick={submit}>Save</Btn>
        </>}>
        {editing && (
          <form onSubmit={submit} className="grid grid-cols-2 gap-4">
            <Field label="Registration Number" required><Input data-testid="veh-reg" value={editing.registration_number} onChange={(e) => setEditing({...editing, registration_number: e.target.value.toUpperCase()})} required /></Field>
            <Field label="Vehicle Name" required><Input data-testid="veh-name" value={editing.vehicle_name} onChange={(e) => setEditing({...editing, vehicle_name: e.target.value})} required /></Field>
            <Field label="Vehicle Type"><Select value={editing.vehicle_type} onChange={(e) => setEditing({...editing, vehicle_type: e.target.value})}>
              {["Sedan","SUV","Hatchback","Tempo Traveller","Bus","Luxury","EV"].map((t) => <option key={t}>{t}</option>)}
            </Select></Field>
            <Field label="Fuel Type"><Select value={editing.fuel_type} onChange={(e) => setEditing({...editing, fuel_type: e.target.value})}>
              {["Petrol","Diesel","CNG","EV","Hybrid"].map((t) => <option key={t}>{t}</option>)}
            </Select></Field>
            <Field label="Brand"><Input value={editing.brand} onChange={(e) => setEditing({...editing, brand: e.target.value})} /></Field>
            <Field label="Model"><Input value={editing.model} onChange={(e) => setEditing({...editing, model: e.target.value})} /></Field>
            <Field label="Seating Capacity"><Input type="number" value={editing.seating_capacity} onChange={(e) => setEditing({...editing, seating_capacity: e.target.value})} /></Field>
            <Field label="Ownership"><Select value={editing.ownership} onChange={(e) => setEditing({...editing, ownership: e.target.value})}>
              <option>Company</option><option>Vendor</option>
            </Select></Field>
            <Field label="RC Expiry"><Input type="date" value={editing.rc_expiry || ""} onChange={(e) => setEditing({...editing, rc_expiry: e.target.value})} /></Field>
            <Field label="Insurance Expiry"><Input type="date" value={editing.insurance_expiry || ""} onChange={(e) => setEditing({...editing, insurance_expiry: e.target.value})} /></Field>
            <Field label="Fitness Expiry"><Input type="date" value={editing.fitness_expiry || ""} onChange={(e) => setEditing({...editing, fitness_expiry: e.target.value})} /></Field>
            <Field label="Permit Expiry"><Input type="date" value={editing.permit_expiry || ""} onChange={(e) => setEditing({...editing, permit_expiry: e.target.value})} /></Field>
            <Field label="PUC Expiry"><Input type="date" value={editing.puc_expiry || ""} onChange={(e) => setEditing({...editing, puc_expiry: e.target.value})} /></Field>
            <Field label="Status"><Select value={editing.status} onChange={(e) => setEditing({...editing, status: e.target.value})}>
              <option value="available">Available</option><option value="on_trip">On Trip</option><option value="maintenance">Maintenance</option>
            </Select></Field>
          </form>
        )}
      </Modal>
    </div>
  );
}
